import React, {useState} from 'react';
import {Node} from 'reactflow';

import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {ProcessFlowchartNodeSection} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import SearchInput from 'ui-library/SearchInput';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {UserFacingNodeTypes} from '../processFlowchartConfigEditorConstants';
import {TaskSchemaNodeData} from '../processFlowchartConfigEditorNodeTypes';
import {NODE_TYPE_TO_SECTION, NODE_SECTION_TO_RENDERER} from '../processFlowchartConfigEditorRegistry';
import {FlowchartOptions} from '../processFlowchartConfigEditorUtils';
import {ConfigurableTaskDefinitionGQL} from '../useAllSchemasById';
import {NodeSidepanelSection} from './NodeSidepanelSection';

interface AddNodeSidepanelContentProps {
  allowedNodeTypes: Array<UserFacingNodeTypes>;
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
  addNodeToCanvas: (newNode: Node<TaskSchemaNodeData>) => void;
  options: FlowchartOptions;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
}

/**
 * A sidepanel component that displays all available nodes that can be added to the flowchart
 */
export default function AddNodeSidepanelContent(props: AddNodeSidepanelContentProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const nodeSectionsAndTypes = props.allowedNodeTypes.reduce(
    (acc: Map<ProcessFlowchartNodeSection, Array<UserFacingNodeTypes>>, nodeType) => {
      const section = NODE_TYPE_TO_SECTION[nodeType];
      if (!acc.has(section)) {
        acc.set(section, []);
      }
      acc.get(section)!.push(nodeType);
      return acc;
    },
    new Map<ProcessFlowchartNodeSection, Array<UserFacingNodeTypes>>()
  );

  return (
    <>
      <SearchInput
        placeholder="Search"
        doSearch={(searchQuery) => {
          setSearchQuery(searchQuery);
        }}
        query={searchQuery}
      />
      <>
        {/* Render in the order the keys are defined in our registry */}
        {(Object.keys(NODE_SECTION_TO_RENDERER) as Array<keyof typeof NODE_SECTION_TO_RENDERER>).map(
          (section) => {
            const nodeTypes = nodeSectionsAndTypes.get(section);
            if (!nodeTypes) {
              return null;
            }
            return (
              <NodeSidepanelSection
                key={section}
                section={section}
                nodeTypes={nodeTypes}
                taskDefinitionsForSidepanel={props.taskDefinitionsForSidepanel}
                addNodeToCanvas={props.addNodeToCanvas}
                options={props.options}
                searchQuery={searchQuery}
                rootTaskSchema={props.rootTaskSchema}
                flowchartEditorConfigurator={props.flowchartEditorConfigurator}
                reactFlowWrapper={props.reactFlowWrapper}
              />
            );
          }
        )}
      </>
    </>
  );
}
